// Single source of truth for "where should this user land?" — used by both the
// Login page (post-login navigate) and App routing (index + catch-all redirects)
// so the two can never drift apart.

type AnyUser = any;

// A "machine viewer" is a login granted one or more companies' machines (machineTenantIds).
// Such a login is dedicated to the Machines page: it only ever sees Machines in the nav,
// lands there on login, and can always reach it regardless of role/permissions.
export const isMachineViewer = (user: AnyUser): boolean =>
  !!user &&
  user.role !== 'SUPERADMIN' &&
  Array.isArray(user.machineTenantIds) &&
  user.machineTenantIds.length > 0;

export const hasModuleView = (user: AnyUser, moduleName: string): boolean => {
  if (!user) return false;
  if (user.role === 'SUPERADMIN') return true;
  return !!(
    user.userRole?.permissions?.some((p: any) => p.module?.name === moduleName && p.canView) ||
    user.permissions?.some((p: any) => p.module?.name === moduleName && p.canView)
  );
};

export const hasModuleCreate = (user: AnyUser, moduleName: string): boolean => {
  if (!user) return false;
  if (user.role === 'SUPERADMIN') return true;
  return !!(
    user.userRole?.permissions?.some((p: any) => p.module?.name === moduleName && p.canCreate) ||
    user.permissions?.some((p: any) => p.module?.name === moduleName && p.canCreate)
  );
};

export const hasAnyModuleAccess = (user: AnyUser): boolean => {
  if (!user) return false;
  return !!(
    user.role === 'SUPERADMIN' ||
    user.role === 'ADMIN' ||
    user.userRole?.permissions?.some((p: any) => p.canView) ||
    user.permissions?.some((p: any) => p.canView)
  );
};

/**
 * Resolve the default landing route for a user.
 * Order:
 *  1. Machine operators -> their machine screen
 *  2. Attendance-only employees (no module access) -> My Attendance
 *  3. Challan (stock module) if accessible
 *  4. Attendance Approvals if that module is accessible
 *  5. Dashboard fallback
 */
export const getHomeRoute = (user: AnyUser): string => {
  if (!user) return '/dashboard';

  // Machine-viewer logins go straight to the Machines page — it's all they can see.
  if (isMachineViewer(user)) return '/machines';

  const hasMachines = hasModuleView(user, 'machines');
  if (user.hasWaterJacket && hasMachines) return '/machines';
  if (user.hasRapierJacket && hasMachines) return '/rapier-machine';

  // Create-only taka operators: can add taka entries but can't view anything. Send them
  // straight to the entry form (before the clock-in-only rule, else they'd get My Attendance).
  if (!hasModuleView(user, 'taka-inventory') && hasModuleCreate(user, 'taka-inventory')) return '/taka-inventory';

  // Clock-in-only staff with no module access
  if (!hasAnyModuleAccess(user) && user.employee?.id) return '/my-attendance';

  // Prefer Challan when the stock module is accessible (existing default)
  if (hasModuleView(user, 'stock')) return '/challan';

  // Send approvers/admins to the approvals queue. Require real approve rights — not just
  // the 'attendance' module view — otherwise a non-approver lands on a page that
  // immediately tells them they lack permission.
  const canApproveAttendance =
    user.role === 'SUPERADMIN' || user.role === 'ADMIN' || !!user.employee?.canApproveAttendance;
  if (canApproveAttendance && hasModuleView(user, 'attendance')) return '/attendance-approvals';

  // A non-approver who still has an employee profile lands on their own attendance page.
  if (user.employee?.id) return '/my-attendance';

  // Dashboard is the final fallback, but it is now permission-gated. Only send users
  // who can actually open it (ADMIN/SUPERADMIN always, or an employee granted the
  // dashboard module); everyone else lands on their profile to avoid an Access Denied screen.
  if (user.role === 'ADMIN' || user.role === 'SUPERADMIN' || hasModuleView(user, 'dashboard')) {
    return '/dashboard';
  }
  return '/profile';
};
